import type { TranslationShape } from '@/lib/i18n/types'

import type { en } from './en'

/**
 * `landing` namespace — Lao. Copy is lifted verbatim from design/code.html,
 * which is the original-language design.
 *
 * `TranslationShape<typeof en>` mirrors the English key tree, so a missing or
 * misspelled key here is a compile error rather than a blank string in production.
 */
export const lo: TranslationShape<typeof en> = {
  meta: {
    title: 'ບໍລິສັດ ຈະເລີນ ໄຊມຸງຄຸນ ຈຳກັດຜູ້ດຽວ — ການຄ້າ, ພະລັງງານ, ອະສັງຫາລິມະຊັບ ແລະ ບໍ່ແຮ່',
    description:
      'ກຸ່ມບໍລິສັດລາວທີ່ໃຫ້ບໍລິການແບບຄົບວົງຈອນ ສ້າງສາອະນາຄົດຜ່ານຄວາມເຂັ້ມແຂງທາງດ້ານອຸດສາຫະກຳ, ພະລັງງານທີ່ຍືນຍົງ ແລະ ຄວາມເປັນເລີດໃນການຄ້າລະດັບໂລກ.',
  },

  brand: {
    name: 'ຈະເລີນ ໄຊມຸງຄຸນ',
    suffix: 'ຈຳກັດຜູ້ດຽວ',
  },

  nav: {
    home: 'ໜ້າຫຼັກ',
    services: 'ການບໍລິການ',
    about: 'ກ່ຽວກັບພວກເຮົາ',
    contact: 'ຕິດຕໍ່ພວກເຮົາ',
    openMenu: 'ເປີດເມນູ',
    closeMenu: 'ປິດເມນູ',
    primaryLabel: 'ເມນູຫຼັກ',
  },

  language: {
    label: 'ພາສາ',
    switchTo: 'ປ່ຽນເປັນ {{language}}',
  },

  hero: {
    title: 'ບໍລິສັດ ຈະເລີນ ໄຊມຸງຄຸນ ຈຳກັດຜູ້ດຽວ',
    subtitle:
      'ສ້າງສາອະນາຄົດຜ່ານຄວາມເຂັ້ມແຂງທາງດ້ານອຸດສາຫະກຳແບບຄົບວົງຈອນ, ພະລັງງານທີ່ຍືນຍົງ ແລະ ຄວາມເປັນເລີດໃນການຄ້າລະດັບໂລກ.',
    ctaPrimary: 'ເບິ່ງການບໍລິການຂອງພວກເຮົາ',
    ctaSecondary: 'ຕິດຕໍ່ພວກເຮົາ',
    carouselLabel: 'ຜົນງານດຳເນີນງານເດັ່ນ',
    previousSlide: 'ຮູບກ່ອນໜ້າ',
    nextSlide: 'ຮູບຕໍ່ໄປ',
    goToSlide: 'ສະແດງຮູບທີ {{number}} ຈາກ {{total}}',
    slides: {
      headquarters: 'ສຳນັກງານໃຫຍ່ຂອງບໍລິສັດໃນຍາມຄ່ຳ ເບິ່ງຈາກຝັ່ງແມ່ນ້ຳ',
      energy: 'ໂຮງງານຜະລິດພະລັງງານແສງອາທິດ ແລະ ພະລັງງານລົມ ໃນຍາມຕາເວັນຕົກ',
      logistics: 'ພາບມູມສູງຂອງສູນກາງການຂົນສົ່ງ ແລະ ກະຈາຍສິນຄ້າຂະໜາດໃຫຍ່',
    },
  },

  about: {
    eyebrow: 'ຂໍ້ມູນບໍລິສັດ',
    heading: 'ຄວາມເຊື່ອໝັ້ນ ແລະ ຄວາມເຂັ້ມແຂງທາງດ້ານອຸດສາຫະກຳ',
    body1:
      'ບໍລິສັດ ຈະເລີນ ໄຊມຸງຄຸນ ຈຳກັດຜູ້ດຽວ ເປັນເສົາຫຼັກຂອງຄວາມເຊື່ອໝັ້ນໃນພາກພື້ນ. ພວກເຮົາເປັນກຸ່ມບໍລິສັດທີ່ໃຫ້ບໍລິການແບບຄົບວົງຈອນ ເພື່ອຊຸກຍູ້ການເຕີບໂຕທາງດ້ານເສດຖະກິດ ໂດຍຮັກສາລະດັບຄວາມຊື່ສັດ ແລະ ຄວາມເປັນເລີດໃນການດຳເນີນງານ.',
    body2:
      'ວິໄສທັດຂອງພວກເຮົາແມ່ນການເຊື່ອມຕໍ່ການດຳເນີນງານຂອງອົງກອນຂະໜາດໃຫຍ່ຢ່າງມີປະສິດທິພາບ. ຜ່ານການຮ່ວມມືແບບໂປ່ງໃສ, ຄວາມເປັນມືອາຊີບ ແລະ ຄວາມມຸ່ງໝັ້ນໃນການພັດທະນາແບບຍືນຍົງ.',
    link: 'ສຶກສາເພີ່ມເຕີມກ່ຽວກັບວິໄສທັດຂອງພວກເຮົາ',
    imageAlt: 'ໂຮງງານພະລັງງານທົດແທນທີ່ທັນສະໄໝ ພ້ອມແຜງແສງອາທິດ ແລະ ກັງຫັນລົມ',
  },

  sectors: {
    eyebrow: 'ຂະແໜງການຫຼັກຂອງພວກເຮົາ',
    heading: 'ວິທີການແກ້ໄຂແບບຄົບວົງຈອນ',
    trading: {
      title: 'ການຄ້າ',
      description: 'ການຄ້າລະດັບໂລກ ແລະ ການຄຸ້ມຄອງຕ່ອງໂສ້ອຸປະທານທີ່ເຂັ້ມແຂງ.',
    },
    energy: {
      title: 'ພະລັງງານ',
      description: 'ການສະໜອງພະລັງງານທີ່ຍືນຍົງ ແລະ ທັນສະໄໝ.',
    },
    realEstate: {
      title: 'ອະສັງຫາລິມະຊັບ',
      description: 'ການພັດທະນາອະສັງຫາລິມະຊັບລະດັບພຣີມຽມ.',
    },
    mining: {
      title: 'ການຂຸດຄົ້ນບໍ່ແຮ່',
      description: 'ການຂຸດຄົ້ນຊັບພະຍາກອນຢ່າງມີຄວາມຮັບຜິດຊອບ ແລະ ມີປະສິດທິພາບ.',
    },
  },

  contact: {
    heading: 'ສຳນັກງານໃຫຍ່',
    intro:
      'ຕິດຕໍ່ທີມງານຂອງພວກເຮົາເພື່ອປຶກສາຫາລືການເປັນຄູ່ຮ່ວມງານ, ການບໍລິການ ແລະ ໂອກາດທາງອຸດສາຫະກຳ.',
    addressLabel: 'ທີ່ຢູ່',
    emailLabel: 'ອີເມວ',
    phoneLabel: 'ເບີໂທລະສັບ',
    websiteLabel: 'ເວັບໄຊ',
    addressLine1: 'ບ້ານ ໂນນຫວາຍ, ເມືອງ ໄຊເສດຖາ',
    addressLine2: 'ນະຄອນຫຼວງວຽງຈັນ, ສປປ ລາວ',
    mapTitle: 'ແຜນທີ່ສະແດງທີ່ຕັ້ງຂອງສຳນັກງານໃຫຍ່',
  },

  footer: {
    copyright: '© {{year}} ບໍລິສັດ ຈະເລີນ ໄຊມຸງຄຸນ ຈຳກັດຜູ້ດຽວ. ສະຫງວນລິຂະສິດ.',
    privacy: 'ນະໂຍບາຍຄວາມເປັນສ່ວນຕົວ',
    terms: 'ເງື່ອນໄຂການບໍລິການ',
    legal: 'ແຈ້ງການທາງກົດໝາຍ',
    sustainability: 'ຄວາມຍືນຍົງ',
  },
}
